import { useId, useRef, useState } from 'react'
import { ApiError, isTransportError } from '@/api/client'
import { useHandregalKorrektur, useHandregalVerlauf, useHandregalZaehlstand } from '@/api/warehouse'
import { parseKorrekturDelta, type KorrekturAuftrag } from '@/lib/handregal-korrektur'
import { newIdempotencyKey } from '@/lib/offline-queue'
import './HandregalKorrekturFeld.css'

const zahl = (value: number) => value < 0 ? `−${Math.abs(value)}` : String(value)
const zeit = (value: string) => {
  const date = new Date(value)
  return `${date.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' })} ${date.toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })}`
}
const arten = { zaehlung: 'Zählung (App)', korrektur: 'Korrektur', inventur: 'Inventur' }

/** Mounted under the same lead guard as the protected counter. */
export default function HandregalKorrekturFeld({ merchantSku, onSaved }: { merchantSku: string; onSaved?: () => void }) {
  const query = useHandregalZaehlstand()
  const mutation = useHandregalKorrektur()
  const [verlaufOffen, setVerlaufOffen] = useState(false)
  const verlauf = useHandregalVerlauf(merchantSku, verlaufOffen)
  const [offen, setOffen] = useState(false)
  const [text, setText] = useState('')
  const [reason, setReason] = useState('')
  const [basis, setBasis] = useState<number | null>(null)
  const [phase, setPhase] = useState<'idle' | 'pending' | 'unclear'>('idle')
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)
  const auftrag = useRef<KorrekturAuftrag | null>(null)
  const laeuft = useRef(false)
  const hinweisId = useId()
  const row = query.data?.zeilen.get(merchantSku)
  const qty = basis ?? row?.physisch ?? 0
  const delta = parseKorrekturDelta(text)
  const ergebnis = delta === null ? null : qty + delta
  const hinweis = text === '' ? null : delta === null ? 'Ganze Zahl eingeben, z. B. +5'
    : ergebnis! < 0 ? 'Ergebnis wäre unter 0'
      : ergebnis! > 1_000_000 || Math.abs(delta) > 1_000_000 ? 'Ergebnis wäre über 1.000.000' : null
  const gesperrt = phase !== 'idle'

  async function senden() {
    if (laeuft.current) return
    if (!auftrag.current) {
      if (hinweis || delta === null || !row || row.physisch === null || reason.trim().length < 5) return
      auftrag.current = { merchantSku, delta, qtyErwartet: qty, idempotencyKey: newIdempotencyKey(), reason: reason.trim() }
    }
    laeuft.current = true
    setPhase('pending')
    setMeldung(null)
    try {
      const antwort = await mutation.mutateAsync(auftrag.current)
      auftrag.current = null
      setPhase('idle')
      if (antwort.blocked) {
        const message = antwort.code === 'stand_geaendert'
          ? `Stand hat sich geändert (jetzt ${antwort.qty_aktuell}). Bitte prüfen und neu buchen.`
          : antwort.code === 'noch_nicht_gezaehlt' ? 'Noch nicht gezählt — erst in der App zählen.'
            : antwort.code === 'idempotency_key_konflikt' ? 'Buchung nicht eindeutig — Seite neu laden und Verlauf prüfen.'
              : antwort.message
        setMeldung({ text: message, fehler: true })
        if (antwort.code === 'stand_geaendert' && antwort.qty_aktuell !== undefined) setBasis(antwort.qty_aktuell)
      } else {
        setMeldung({ text: `Gebucht: ${antwort.qty_vorher} → ${antwort.qty}`, fehler: false })
        setOffen(false)
        setText('')
        setReason('')
        setBasis(null)
        onSaved?.()
      }
    } catch (error) {
      const unklar = isTransportError(error) || !(error instanceof ApiError && error.status >= 400 && error.status < 500)
      if (unklar) {
        setPhase('unclear')
        setMeldung({ text: 'Ausgang unklar. Erneut senden oder Verlauf prüfen.', fehler: true })
      } else {
        auftrag.current = null
        setPhase('idle')
        setMeldung({ text: error.message, fehler: true })
      }
    } finally {
      laeuft.current = false
    }
  }

  function abbrechen() {
    if (gesperrt || laeuft.current) return
    setOffen(false)
    setText('')
    setReason('')
    setBasis(null)
    setMeldung(null)
  }

  return <div data-testid="handregal-korrektur-feld" className="korrektur-feld">
    {query.isError && !row ? <div role="alert">Zählstand konnte nicht geladen werden. Bitte erneut laden.</div>
      : !query.data ? <div role="status">Lade Zählstand …</div>
        : !row ? <div className="korrektur-standard">noch nicht gezählt — erst in der App zählen</div>
          : row.physisch === null ? <div className="korrektur-standard">Physischer Bestand nicht verfügbar.</div>
          : !offen ? <button type="button" onClick={() => { setBasis(row.physisch); setOffen(true); setMeldung(null) }}>Menge korrigieren</button>
            : <form aria-label="Menge korrigieren" aria-busy={phase === 'pending'} onSubmit={e => {
              e.preventDefault()
              if (!gesperrt) void senden()
            }} onKeyDown={e => {
              if (e.key === 'Escape') { e.preventDefault(); abbrechen() }
            }}>
              <input type="text" autoFocus aria-label="Korrektur in Stück" placeholder="+5 oder −3" value={text}
                className="korrektur-mono" disabled={gesperrt} aria-invalid={Boolean(hinweis)} aria-describedby={hinweisId}
                onChange={e => { if (!gesperrt && !laeuft.current) { setText(e.target.value); setMeldung(null) } }} />
              <input type="text" aria-label="Grund" placeholder="Grund (mindestens 5 Zeichen)" maxLength={200}
                value={reason} disabled={gesperrt} onChange={e => setReason(e.target.value)} />
              <div id={hinweisId}>
                {meldung?.fehler ? <div role="alert">{meldung.text}</div>
                  : hinweis && <div role="alert">{hinweis}</div>}
              </div>
              {delta !== null && <div className="korrektur-rechnung korrektur-mono">physisch {qty} {delta < 0 ? '−' : '+'} {Math.abs(delta)} = <b>{zahl(ergebnis!)}</b></div>}
              <div className="korrektur-aktionen">
                <button type="submit" disabled={gesperrt || delta === null || Boolean(hinweis) || reason.trim().length < 5}>{delta === null ? 'buchen' : `${delta > 0 ? '+' : ''}${zahl(delta)} buchen`}</button>
                <button type="button" disabled={gesperrt} onClick={abbrechen}>Abbrechen</button>
                {phase === 'unclear' && <>
                  <button type="button" onClick={() => void senden()}>Erneut senden</button>
                  <button type="button" onClick={() => {
                    // Keep the original comparison value even if invalidation already found the committed quantity.
                    auftrag.current = null
                    setPhase('idle')
                    setMeldung(null)
                  }}>Verwerfen</button>
                </>}
              </div>
            </form>}
    {meldung && !meldung.fehler && <div aria-live="polite" className="korrektur-erfolg korrektur-standard">{meldung.text}</div>}
    <details className="korrektur-verlauf korrektur-standard" onToggle={e => setVerlaufOffen(e.currentTarget.open)}>
      <summary>Verlauf</summary>
      {verlaufOffen && <div className="korrektur-eintraege korrektur-mono">
        {verlauf.isError ? <div role="alert">Verlauf konnte nicht geladen werden. Bitte erneut öffnen.</div>
          : !verlauf.data ? <div role="status">Lade Verlauf …</div>
            : verlauf.data.length === 0 ? <div>Keine Einträge.</div>
              : verlauf.data.map(e => <div key={e.id}>{zeit(e.at)} · {arten[e.art]} · {e.qty_vorher ?? '—'} → {e.qty_nachher ?? '—'} · {e.wer ?? 'unbekannt'}{e.geraet ? ` · ${e.geraet}` : ''}{e.grund ? ` · ${e.grund}` : ''}</div>)}
      </div>}
    </details>
  </div>
}
